import { useQuery } from "@tanstack/react-query";
import { searchQuery, SEARCH_QUERY_KEY } from "../api/searchV2Api";
import type { SearchContext, SearchQueryParams, SearchQueryResponse } from "../types";
import type { ServerResponse } from "../../../types";

function extractResults(res: Awaited<ReturnType<typeof searchQuery>>) {
  return (res.data as unknown as ServerResponse<SearchQueryResponse>).payload
    .results;
}

export function useInstantSearch(
  q: string,
  context?: SearchContext,
  filters?: Record<string, number>,
) {
  const baseParams: SearchQueryParams = {
    q,
    context,
    perpage: 10,
    enrich: false,
    ...filters,
  };

  // Phase 1: keyword-only (fast)
  const keyword = useQuery({
    queryKey: [...SEARCH_QUERY_KEY, "instant", q, context, filters],
    queryFn: ({ signal }) => searchQuery(baseParams, signal),
    enabled: q.length >= 2,
    select: extractResults,
    staleTime: 30_000,
  });

  // Phase 2: semantic follow-up (only when keyword came back non-semantic)
  const needsSemantic = keyword.data !== undefined && !keyword.data.semantic;

  const semantic = useQuery({
    queryKey: [...SEARCH_QUERY_KEY, "instant", "semantic", q, context, filters],
    queryFn: ({ signal }) =>
      searchQuery({ ...baseParams, semantic: true }, signal),
    enabled: needsSemantic,
    select: extractResults,
    staleTime: 60_000,
  });

  // Prefer semantic results when available, fall back to keyword
  const data = semantic.data ?? keyword.data;
  const isLoading = keyword.isLoading;

  return { data, isLoading };
}
